/**
 * SubDelegateSignupForm — anonymous accept form (plan §7.1).
 * Composes shared invite-signup primitives. Read-only pre-filled fields + ToS.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  InviteSignupShell,
  InviteSignupField,
  InviterBadge,
  TermsCheckbox,
} from '@/shared/components/invite-signup';
import type { SubDelegateInvitePublicResponse } from '../types/subDelegateInvite';
import { ownerRoleLabel, permissionLabels } from '../utils/labels';

export type SubDelegateSignupFormProps = {
  invite: SubDelegateInvitePublicResponse;
  isSubmitting: boolean;
  apiError?: string;
  onSubmit: () => void;
};

export const SubDelegateSignupForm = ({
  invite,
  isSubmitting,
  apiError,
  onSubmit,
}: SubDelegateSignupFormProps) => {
  const [termsAgreed, setTermsAgreed] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!termsAgreed) return;
    onSubmit();
  };

  return (
    <InviteSignupShell>
      <InviterBadge
        name={invite.ownerName}
        secondary={ownerRoleLabel(invite.ownerRole)}
        permissions={permissionLabels(invite.permissions)}
      />

      <h2 className="font-recoleta text-white md:w-full text-3xl md:text-4xl">
        You've Been Invited
      </h2>

      <p className="font-termina text-white max-md:text-xl max-md:py-8 py-6">
        Complete your registration to manage <strong>{invite.ownerName}</strong>
        's bQuest workspace as a sub-delegate.
      </p>

      <p className="font-termina text-white">
        Already have an account?{' '}
        <Link
          className="font-termina text-white hover:font-bold underline"
          to="/login"
        >
          Sign In
        </Link>
      </p>

      {apiError && (
        <p className="font-termina text-red-500 font-bold py-4">{apiError}</p>
      )}

      <form className="py-12 gap-6 flex flex-col" onSubmit={handleSubmit}>
        <InviteSignupField
          label="First Name"
          htmlFor="sub-delegate-first-name"
          helperText="You can update your name after registration"
        >
          <input
            id="sub-delegate-first-name"
            className="rounded-3xl font-termina py-3 px-5 text-base bg-white/80"
            type="text"
            value={invite.firstName}
            readOnly
          />
        </InviteSignupField>

        <InviteSignupField
          label="Last Name"
          htmlFor="sub-delegate-last-name"
        >
          <input
            id="sub-delegate-last-name"
            className="rounded-3xl font-termina py-3 px-5 text-base bg-white/80"
            type="text"
            value={invite.lastName}
            readOnly
          />
        </InviteSignupField>

        <InviteSignupField
          label="Email Address"
          htmlFor="sub-delegate-email"
          helperText="Your account will be linked to this email address"
        >
          <input
            id="sub-delegate-email"
            className="rounded-3xl font-termina py-3 px-5 text-base bg-white/80 cursor-not-allowed"
            type="email"
            value={invite.email}
            readOnly
          />
        </InviteSignupField>

        <TermsCheckbox checked={termsAgreed} onChange={setTermsAgreed} />

        <button
          type="submit"
          className="font-termina bg-[#E2A807] font-black rounded-3xl py-3 px-6 w-max uppercase cursor-pointer hover:bg-[#c99006] active:scale-[0.98] transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#E2A807] disabled:active:scale-100"
          disabled={!termsAgreed || isSubmitting}
        >
          {isSubmitting ? 'Creating Account...' : 'Create Sub-Delegate Account'}
        </button>
      </form>

      <p className="font-termina text-white/70 text-sm">
        By accepting this invitation, you'll be able to act on behalf of{' '}
        {invite.ownerName}. Every action you take is attributed back to them and
        logged in their workspace audit trail.
      </p>
    </InviteSignupShell>
  );
};
